import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { format } from "date-fns";
import SidePanel from "../../components/sidePanel";
import { ConfirmModal } from "../../components/confirmModal";
import Spinner from "../../components/spinner";
import PLANS from "../../api/services/PLANS";
import BILLING from "../../api/services/BILLING";
import styles from "../../styles/_containers/_clientBilling/planDetailPanel.module.scss";

const PlanDetailPanel = ({ plan, userId, setIsOpen, onSuccess }) => {
  const [freezeLoading, setFreezeLoading] = useState(false);
  const [renewLoading, setRenewLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [usageHistory, setUsageHistory] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);

  useEffect(() => {
    if (!plan._id) return;
    let cancelled = false;
    const fetchUsage = async () => {
      setUsageLoading(true);
      try {
        const res = await BILLING.get_plan_usage_history(plan._id);
        if (!cancelled) setUsageHistory(res.data || []);
      } catch (err) {
        console.log(err);
      }
      if (!cancelled) setUsageLoading(false);
    };
    fetchUsage();
    return () => { cancelled = true; };
  }, [plan._id]);

  const getStatusInfo = (status) => {
    switch (status) {
      case 1: return { icon: "\u2713", label: "\u05D1\u05D5\u05E6\u05E2", className: styles.statusCompleted };
      case 2: return { icon: "\u2717", label: "\u05D1\u05D5\u05D8\u05DC", className: styles.statusCancelled };
      default: return { icon: "\u23F3", label: "\u05E2\u05EA\u05D9\u05D3\u05D9", className: styles.statusUpcoming };
    }
  };

  const isSub =
    plan.isSubscription || plan.baseServicePlanId?.isSubscription;
  const isExpired =
    plan.expiresAt && new Date(plan.expiresAt) < new Date();
  const remaining =
    plan.numberOfTurns > 0
      ? plan.numberOfTurns - (plan.consumedOneTimeTurns || 0)
      : null;
  const usagePercent =
    plan.numberOfTurns > 0
      ? ((plan.consumedOneTimeTurns || 0) / plan.numberOfTurns) * 100
      : 0;

  const formatDate = (d) => {
    if (!d) return "---";
    try {
      return format(new Date(d), "dd/MM/yyyy");
    } catch {
      return "---";
    }
  };

  // ---------- Freeze / Unfreeze ----------
  const handleFreeze = async () => {
    setFreezeLoading(true);
    try {
      const res = await PLANS.freezeUnFreezePlan({
        userServicePlanId: plan._id,
        freeze: !plan.isFrozen,
      });
      toast.success(res.data.message);
      onSuccess?.();
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || "שגיאה בביצוע הפעולה");
    }
    setFreezeLoading(false);
  };

  // ---------- Renew ----------
  const handleRenew = async () => {
    setRenewLoading(true);
    try {
      const res = isSub
        ? await PLANS.renewSubscriptionPlan(plan._id)
        : await PLANS.renewCardPlan(plan._id);
      toast.success(res.data.message);
      onSuccess?.();
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || "שגיאה בחידוש");
    }
    setRenewLoading(false);
  };

  // ---------- Cancel ----------
  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      const res = await PLANS.cancelMembership({
        userId,
        userServicePlanId: plan._id,
      });
      toast.success(res.data.message);
      onSuccess?.();
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || "שגיאה בביטול");
    }
    setCancelLoading(false);
    setShowConfirm(false);
  };

  // ---------- Usage bar color ----------
  const getBarColor = () => {
    if (usagePercent >= 80) return "#ef4444";
    if (usagePercent >= 50) return "#f59e0b";
    return "#22c55e";
  };

  const renewDisabled =
    plan.isFrozen || (isSub && plan.isActive && !isExpired);

  return (
    <SidePanel setIsOpen={setIsOpen} title="פרטי מנוי">
      <div className={styles.wrapper}>
        {/* ---------- Name ---------- */}
        <h3 className={styles.planName}>{plan.name}</h3>

        {/* ---------- Badges ---------- */}
        <div className={styles.badges}>
          <span className={`${styles.badge} ${isSub ? styles.sub : styles.card}`}>
            {isSub ? "מנוי" : "כרטיסייה"}
          </span>

          {plan.isFrozen && (
            <span className={`${styles.badge} ${styles.frozen}`}>מוקפא</span>
          )}

          {isExpired ? (
            <span className={`${styles.badge} ${styles.expired}`}>פג תוקף</span>
          ) : plan.isActive ? (
            <span className={`${styles.badge} ${styles.active}`}>פעיל</span>
          ) : (
            <span className={`${styles.badge} ${styles.inactive}`}>לא פעיל</span>
          )}

          {plan.hasPaid && (
            <span className={`${styles.badge} ${styles.paid}`}>שולם</span>
          )}
        </div>

        {/* ---------- Info grid ---------- */}
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.label}>מחיר</span>
            <span className={styles.value}>₪{plan.price}</span>
          </div>

          <div className={styles.infoItem}>
            <span className={styles.label}>תאריך רכישה</span>
            <span className={styles.value}>{formatDate(plan.paidAt || plan.createdAt)}</span>
          </div>

          {plan.expiresAt && (
            <div className={styles.infoItem}>
              <span className={styles.label}>תוקף</span>
              <span className={`${styles.value} ${isExpired ? styles.expiredText : ""}`}>
                {formatDate(plan.expiresAt)}
              </span>
            </div>
          )}

          {plan.isFrozen && plan.frozenAt && (
            <div className={styles.infoItem}>
              <span className={styles.label}>הוקפא בתאריך</span>
              <span className={styles.value}>{formatDate(plan.frozenAt)}</span>
            </div>
          )}
        </div>

        {/* ---------- Usage ---------- */}
        {!isSub && plan.numberOfTurns > 0 && (
          <div className={styles.usageSection}>
            <div className={styles.usageHeader}>
              <span className={styles.usageTitle}>מימושים</span>
              <span className={styles.usageCount}>
                {plan.consumedOneTimeTurns || 0} / {plan.numberOfTurns}
              </span>
            </div>
            <div className={styles.usageBarTrack}>
              <div
                className={styles.usageBarFill}
                style={{
                  width: `${Math.min(usagePercent, 100)}%`,
                  backgroundColor: getBarColor(),
                }}
              />
            </div>
            {remaining !== null && (
              <span className={styles.usageRemaining}>
                {remaining > 0 ? `נותרו ${remaining} מימושים` : "כל המימושים נוצלו"}
              </span>
            )}
          </div>
        )}

        {/* ---------- Usage history ---------- */}
        <div className={styles.usageHistorySection}>
          <span className={styles.usageHistoryTitle}>היסטוריית שימוש</span>
          {usageLoading ? (
            <div className={styles.usageLoading}><Spinner /></div>
          ) : usageHistory.length === 0 ? (
            <span className={styles.usageEmpty}>אין שימוש עדיין</span>
          ) : (
            <div className={styles.usageList}>
              <div className={`${styles.usageItem} ${styles.usageItemHeader}`}>
                <span>תאריך</span>
                <span>שעה</span>
                <span>שירות</span>
                <span>סטטוס</span>
              </div>
              {usageHistory.map((item, i) => {
                const statusInfo = getStatusInfo(item.status);
                return (
                  <div key={i} className={styles.usageItem}>
                    <span>{formatDate(item.date)}</span>
                    <span>{item.timeSlot || "---"}</span>
                    <span className={styles.usageServiceName}>
                      {item.serviceId?.name || "---"}
                    </span>
                    <span className={statusInfo.className}>
                      {statusInfo.icon} {statusInfo.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ---------- Freeze history ---------- */}
        {plan.freezeUnFreezeHistory?.length > 0 && (
          <div className={styles.historySection}>
            <span className={styles.historyTitle}>היסטוריית הקפאות</span>
            <ul className={styles.historyList}>
              {plan.freezeUnFreezeHistory.map((date, i) => (
                <li key={i} className={styles.historyItem}>
                  <span className={styles.historyDot} />
                  <span>{formatDate(date)}</span>
                  <span className={styles.historyLabel}>
                    {i % 2 === 0 ? "הקפאה" : "הפשרה"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ---------- Actions ---------- */}
        <div className={styles.actions}>
          <button
            className={styles.freezeBtn}
            onClick={handleFreeze}
            disabled={freezeLoading}
          >
            {freezeLoading ? (
              <Spinner />
            ) : plan.isFrozen ? (
              "הפשרת מנוי"
            ) : (
              "הקפאת מנוי"
            )}
          </button>

          <button
            className={styles.renewBtn}
            onClick={handleRenew}
            disabled={renewLoading || renewDisabled}
          >
            {renewLoading ? <Spinner /> : "חידוש מנוי"}
          </button>

          <button
            className={styles.cancelBtn}
            onClick={() => setShowConfirm(true)}
            disabled={cancelLoading}
          >
            {cancelLoading ? <Spinner /> : "ביטול מנוי"}
          </button>
        </div>
      </div>

      {/* ---------- Confirm cancel ---------- */}
      {showConfirm && (
        <ConfirmModal
          setIsOpen={setShowConfirm}
          handleClick={handleCancel}
          loading={cancelLoading}
        />
      )}
    </SidePanel>
  );
};

export default PlanDetailPanel;
